import { useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { WORKSPACE_DETAILS_FORM_FIELDS } from '../../shared/fieldNames';
import { WorkspaceDetailsDependencyContext } from '../../shared/types';

/**
 * When the proceed gate flips, re-validate the business-number and entity-name
 * fields whose required-ness depends on it (FR-009). The gate value itself is
 * (re)computed by the country/entityType dependency hooks and the lookup.
 *
 * The initial gate value is computed in shared/defaults.ts, replacing the
 * legacy load effect at WorkspaceDetailsForm.tsx:251-272; the re-trigger here
 * replaces the inline trigger('businessNumber') in WorkspaceDetailsForm.tsx:420-433.
 */
export const useCanProceedDependencies = ({
  form,
}: WorkspaceDetailsDependencyContext) => {
  const { control, getFieldState, trigger } = form;
  const canProceed = useWatch({
    control,
    name: WORKSPACE_DETAILS_FORM_FIELDS.canProceed,
  });
  const previous = useRef(canProceed);

  useEffect(() => {
    if (previous.current === canProceed) return;
    previous.current = canProceed;

    /**
     * Re-validation exists to refresh or clear an error the gate has just made
     * stale — it must never raise the *first* error on a field the user has
     * not reached yet. Legacy only re-validated from the lookup's proceed
     * callback, where the business number is necessarily already filled in;
     * here the gate also closes when a country is picked, and validating an
     * empty untouched field fired "Please enter ABN" the moment AU was
     * selected (APP-2237 QA).
     *
     * `isTouched` (blur) rather than `isDirty`: the country dependency blanks
     * these fields with `shouldDirty`, so dirtiness marks our own resets as
     * user engagement and reintroduces the same problem on the entity name.
     */
    const revalidateIfEngaged = (
      name:
        | typeof WORKSPACE_DETAILS_FORM_FIELDS.businessNumber
        | typeof WORKSPACE_DETAILS_FORM_FIELDS.entityName,
    ) => {
      const { error, isTouched } = getFieldState(name);

      if (!error && !isTouched) return;
      void trigger(name);
    };

    revalidateIfEngaged(WORKSPACE_DETAILS_FORM_FIELDS.businessNumber);
    revalidateIfEngaged(WORKSPACE_DETAILS_FORM_FIELDS.entityName);
  }, [canProceed, getFieldState, trigger]);
};
